import { api } from '@tg/app-core';
import type { QueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { addDays, today } from '../lib/format';
import { PACK_PATH, dayKey, rebasePack, type Pack } from './days';
import { idbGet, idbSet } from './idb';
import { isOnline, onNet } from './net';
import { kick, pendingOps, serverRows } from './outbox';

/* Синхронизация кабинета с сервером и что показывает плашка вверху:
   offline — связи нет; syncing — связь вернулась, уходит очередь и подтягиваются свежие данные;
   done — всё сохранено и данные с сервера (пара секунд), затем плашка пропадает.
   Пока связь есть, раз в 10 минут подкачиваем окно дней для работы без сети (offlinePack). */

export type Phase = 'idle' | 'offline' | 'syncing' | 'done';
interface SyncState {
  phase: Phase;
  /** Когда данные на устройстве последний раз обновлялись с сервера */
  syncedAt: number | null;
}

/** Окно дней на устройстве: две недели назад и шесть вперёд */
const BACK = 14;
const AHEAD = 42;
const EVERY = 10 * 60_000;

let qc: QueryClient | null = null;
let state: SyncState = { phase: isOnline() ? 'idle' : 'offline', syncedAt: null };
const subs = new Set<() => void>();
let doneTimer: ReturnType<typeof setTimeout> | undefined;
let every: ReturnType<typeof setInterval> | undefined;

function emit(p: Partial<SyncState>) {
  state = { ...state, ...p };
  subs.forEach((f) => f());
}

/** Окно дней всех клиентов и групп — на устройство */
async function loadPack() {
  if (!qc || !isOnline()) return;
  const input = { from: addDays(today(), -BACK), to: addDays(today(), AHEAD) };
  const key = [PACK_PATH, { input, type: 'query' }];
  try {
    await qc.fetchQuery({
      queryKey: key,
      queryFn: async () => {
        const p = (await api.trainer.offlinePack.query(input)) as Pack;
        serverRows(p.days.map((r) => ({ key: dayKey(r.clientId ? { clientId: r.clientId } : { groupId: r.groupId! }, r.date), date: r.date, rev: r.rev })));
        return rebasePack(p, pendingOps());
      },
      staleTime: 0,
    });
    /* Окна прошлых дней больше не нужны */
    qc.removeQueries({ queryKey: [PACK_PATH], predicate: (q) => JSON.stringify(q.queryKey) !== JSON.stringify(key) });
    const at = Date.now();
    idbSet('meta', 'syncedAt', at);
    emit({ syncedAt: at });
  } catch {
    /* не дошло — данные на устройстве остаются прежними */
  }
}

/** Связь вернулась: очередь по порядку, затем свежие данные */
async function resync() {
  clearTimeout(doneTimer);
  emit({ phase: 'syncing' });
  await kick();
  if (!isOnline()) return emit({ phase: 'offline' });
  await qc?.invalidateQueries({ queryKey: [['trainer']] });
  await loadPack();
  if (!isOnline()) return emit({ phase: 'offline' });
  /* Остались правки (конфликт, ждут входа) — плашка «синхронизация» остаётся, пока их не разберут */
  if (pendingOps().length) return;
  emit({ phase: 'done' });
  doneTimer = setTimeout(() => emit({ phase: 'idle' }), 3000);
}

/** Аккаунт открыт: время прошлой синхронизации с устройства, подкачка окна дней */
export async function initSync(client: QueryClient) {
  qc = client;
  emit({ syncedAt: (await idbGet<number>('meta', 'syncedAt')) ?? null, phase: isOnline() ? 'idle' : 'offline' });
  clearInterval(every);
  every = setInterval(() => {
    if (isOnline() && !pendingOps().length) loadPack();
  }, EVERY);
  if (isOnline()) {
    if (pendingOps().length) resync();
    else loadPack();
  }
}

export function stopSync() {
  qc = null;
  clearInterval(every);
  emit({ phase: isOnline() ? 'idle' : 'offline', syncedAt: null });
}

/** Очередь разобрана после конфликта — довести синхронизацию */
export function afterConflict() {
  if (isOnline() && state.phase === 'syncing') resync();
}

onNet(() => {
  if (!isOnline()) {
    clearTimeout(doneTimer);
    emit({ phase: 'offline' });
  } else if (state.phase === 'offline') resync();
});

function onSync(f: () => void) {
  subs.add(f);
  return () => {
    subs.delete(f);
  };
}
export const useSync = () => useSyncExternalStore(onSync, () => state);
